/**
 * AI CONTEXT
 * Purpose: evaluation operations page (filter/sort/paginate/inline promotion).
 * Key deps: EvaluationViewContextProvider, EvaluationClassCard.
 * Referenced by: App route ROUTES.EVALUATIONS.
 * Importance: core grading/evaluation workflow.
 * Refactor: split filter toolbar component.
 */
import { BarChartOutlined, SortAscendingOutlined } from "@ant-design/icons"
import { Button, Popover, Select, Tag, Tooltip } from "antd"
import { useMemo, useState } from "react"
import Masonry from "react-masonry-css"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import PageSpinner from "#blocks/PageSpinner"
import Pagination from "#blocks/Pagination"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { EVALUATION_STATUS } from "#customTypes/composedTypes/evaluationTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { buildClassroomRoster } from "#utils/classroomPromotions"
import { getEvaluationStatusColor } from "#utils/evaluationHelpers"
import { filterFormOptions } from "#utils/formHelpers"
import { addOneSemester, formatSemester } from "#utils/semesterHelpers"
import { camelToWords } from "#utils/textFormatters"

import ClassroomPromotions from "./ClassroomPromotions"
import EvaluationClassCard, {
  EVALUATION_CARD_WIDTH,
} from "./EvaluationClassCard"
import EvaluationWorkloadDashboard from "./EvaluationWorkloadDashboard"
import useCanViewEvaluationWorkloadDashboard from "./useCanViewEvaluationWorkloadDashboard"
import useEvaluationViewContext, {
  EvaluationViewContextProvider,
} from "./useEvaluationViewContext"

/**
 * The page is 95vw. A column count is safe only once that width can hold the
 * cards, and the cap has to be at least three cards or the third one is clipped.
 */
const windowWidthForCards = (cardCount: number) =>
  Math.ceil((EVALUATION_CARD_WIDTH * cardCount) / 0.95)

const MASONRY_COLUMNS = {
  default: 3,
  [windowWidthForCards(2) - 1]: 1,
  [windowWidthForCards(3) - 1]: 2,
}

const STATUS_OPTIONS = Object.values(EVALUATION_STATUS).map((status) => ({
  label: camelToWords(status),
  value: status,
}))

const EvaluationViewBase = () => {
  const { canRead } = useResourcePermissions(RESOURCES.EVALUATION)
  const canViewDashboard = useCanViewEvaluationWorkloadDashboard()

  const {
    classesForTable,
    classesMatchingSearch,
    evaluationsById,
    studentsById,
    onSearch,
    isReady,
    isDashboardReady,
    currentPage,
    totalPages,
    onPageChange,
    totalResults,
    statusFilters,
    teacherFilters,
    approverFilters,
    setStatusFilters,
    setTeacherFilters,
    setApproverFilters,
    onClearFilters,
    availableTeacherOptions,
    availableApproverOptions,
    sortByStatus,
    toggleSortByStatus,
  } = useEvaluationViewContext((state) => ({
    classesForTable: state.classesForTable,
    classesMatchingSearch: state.classesMatchingSearch,
    evaluationsById: state.evaluationsById,
    studentsById: state.studentsById,
    onSearch: state.onSearch,
    isReady: state.isReady,
    isDashboardReady: state.isDashboardReady,
    currentPage: state.currentPage,
    totalPages: state.totalPages,
    onPageChange: state.onPageChange,
    totalResults: state.totalResults,
    statusFilters: state.statusFilters,
    teacherFilters: state.teacherFilters,
    approverFilters: state.approverFilters,
    setStatusFilters: state.setStatusFilters,
    setTeacherFilters: state.setTeacherFilters,
    setApproverFilters: state.setApproverFilters,
    onClearFilters: state.onClearFilters,
    availableTeacherOptions: state.availableTeacherOptions,
    availableApproverOptions: state.availableApproverOptions,
    sortByStatus: state.sortByStatus,
    toggleSortByStatus: state.toggleSortByStatus,
  }))

  const [pageTab, setPageTab] = useState<"evaluations" | "classroom">(
    "evaluations"
  )

  const classroomClasses = useMemo(
    () =>
      buildClassroomRoster(
        classesMatchingSearch.map((item) => item.classObj),
        studentsById,
        evaluationsById
      ),
    [classesMatchingSearch, studentsById, evaluationsById]
  )
  const readyCount = classroomClasses.filter(
    (classObj) => classObj.ready
  ).length
  const semester = classesMatchingSearch[0]?.classObj.semester
  const nextSemester = semester ? addOneSemester(semester) : undefined

  const statusTagRender = useMemo(
    () =>
      ({
        label,
        value,
        closable,
        onClose,
      }: {
        label: React.ReactNode
        value: string
        closable: boolean
        onClose: () => void
      }) => (
        <Tag
          color={getEvaluationStatusColor(value)}
          closable={closable}
          onClose={onClose}
          style={{ marginInlineEnd: 4 }}
        >
          {label}
        </Tag>
      ),
    []
  )

  if (!canRead) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex
        col
        style={{
          margin: 20,
          width: "95vw",
          maxWidth: EVALUATION_CARD_WIDTH * 3,
        }}
        gap={12}
      >
        <h1>Evaluations</h1>
        <Flex gap={8}>
          <Button
            type={pageTab === "evaluations" ? "primary" : "default"}
            onClick={() => setPageTab("evaluations")}
          >
            Evaluations
          </Button>
          <Button
            type={pageTab === "classroom" ? "primary" : "default"}
            onClick={() => setPageTab("classroom")}
          >
            Classroom
            {readyCount > 0 ? (
              <Tag style={{ marginInlineStart: 8 }}>{readyCount} ready</Tag>
            ) : null}
          </Button>
        </Flex>

        <Flex
          col
          gap={14}
          style={{ justifyContent: "center", flexWrap: "wrap" }}
        >
          <TableFiltersRow
            onClear={onClearFilters}
            onSearch={onSearch}
            searchPlaceholder="Search by class name, student name, or teacher name"
            settingsKey={SETTINGS_KEYS.EVALUATIONS}
          />

          <Flex gap={12}>
            <Select
              mode="multiple"
              placeholder="Filter by teacher"
              value={teacherFilters}
              onChange={setTeacherFilters}
              options={availableTeacherOptions}
              filterOption={filterFormOptions}
              style={{ minWidth: 170 }}
              allowClear
              showSearch
              maxTagCount="responsive"
            />

            <Select
              mode="multiple"
              placeholder="Filter by approver"
              value={approverFilters}
              onChange={setApproverFilters}
              options={availableApproverOptions}
              filterOption={filterFormOptions}
              style={{ minWidth: 170 }}
              allowClear
              showSearch
              maxTagCount="responsive"
            />

            <Select
              mode="multiple"
              placeholder="Filter by status"
              value={statusFilters}
              onChange={setStatusFilters}
              options={STATUS_OPTIONS}
              tagRender={statusTagRender}
              style={{ minWidth: 400 }}
              allowClear
              maxTagCount="responsive"
              optionRender={(option) => (
                <Tag color={getEvaluationStatusColor(option.value as string)}>
                  {option.label}
                </Tag>
              )}
            />

            <Tooltip
              title={
                sortByStatus
                  ? "Sorting by status, then name"
                  : "Sorting alphabetically"
              }
            >
              <Button
                type={sortByStatus ? "primary" : "default"}
                icon={<SortAscendingOutlined />}
                onClick={toggleSortByStatus}
              >
                {sortByStatus ? "Status" : "A-Z"}
              </Button>
            </Tooltip>

            {canViewDashboard && (
              <Tooltip
                title={
                  isDashboardReady
                    ? "View workload snapshot"
                    : "Loading class/session data..."
                }
              >
                <Popover
                  trigger="click"
                  destroyTooltipOnHide
                  content={<EvaluationWorkloadDashboard />}
                >
                  <Button
                    icon={<BarChartOutlined />}
                    loading={!isDashboardReady}
                    disabled={!isDashboardReady}
                  >
                    Dashboard
                  </Button>
                </Popover>
              </Tooltip>
            )}
          </Flex>
        </Flex>

        {!isReady ? (
          <PageSpinner />
        ) : pageTab === "classroom" ? (
          <ClassroomPromotions
            classes={classroomClasses}
            semesterLabel={formatSemester(semester)}
            nextSemesterLabel={
              nextSemester
                ? `Levels take effect ${formatSemester(nextSemester)}`
                : "No next semester on the calendar"
            }
          />
        ) : (
          <>
            <Flex gap={16} style={{ alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: "#666" }}>
                Showing {classesForTable.length} of {totalResults} classes
              </span>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                disabled={!isReady}
              />
            </Flex>

            <Masonry
              breakpointCols={MASONRY_COLUMNS}
              className="masonry-grid"
              columnClassName="masonry-grid_column"
              style={{ display: "flex", overflowY: "auto" }}
            >
              {classesForTable.map(({ classObj }) => (
                <EvaluationClassCard key={classObj.id} item={classObj} />
              ))}
            </Masonry>
          </>
        )}
      </Flex>
    </Page>
  )
}

const EvaluationView = () => {
  return (
    <EvaluationViewContextProvider>
      <EvaluationViewBase />
    </EvaluationViewContextProvider>
  )
}

export default EvaluationView
